import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, DatePicker, Input, Modal, Select, Skeleton, Tag, Tooltip, Typography } from 'antd';
import Axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { CalendarClock, ClipboardList, Inbox, PackageCheck, ShieldCheck } from 'lucide-react';
import { useTheme } from '@/App';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });

const PRIORITY = { LOW: ['default', 'Low'], NORMAL: ['blue', 'Normal'], HIGH: ['orange', 'High'], URGENT: ['red', 'Urgent'] };
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const fmtDate = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');
const fmtDateTime = (v) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—');
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));

/** `packaging_order_ref` is free text and the seed writes a comma-separated list of batch numbers. */
const packagingRefs = (v) => String(v || '').split(',').map((s) => s.trim()).filter(Boolean);

/** The frozen header stores decimals verbatim ("2.0000 mg") — trim the trailing zeros for display only. */
const tidy = (v) => String(v || '').replace(/(\d+)\.(\d*?)0+(?=\s|$)/g, (m, i, d) => (d ? `${i}.${d}` : i));

const PRINT_CSS = `
@media print {
  .bmr-order-header { break-inside: avoid; page-break-inside: avoid; background: #fff !important; border: 1px solid #999 !important; }
  .bmr-order-header * { color: #000 !important; }
  .bmr-order-header .ant-tag { border: 1px solid #666 !important; background: #fff !important; }
  .bmr-order-header .bmr-order-noprint { display: none !important; }
}`;

/**
 * Release-BMR header block on /batch-process-details/<batch> — the order metadata the batch was issued
 * against (PO / SO / customer / market / campaign / planned window / target release / priority), the
 * product + MBMR identity, the change-control reference, the packaging batches this bulk feeds, and the
 * expiry. The expiry is tagged PROVISIONAL until the frozen mfg-date rule has fired
 * (header_freeze.stamp_mfg_date), and shows the real value with its Mfg date afterwards.
 *
 * Read-only and print-friendly; when the batch has no BatchOrderInfo row it offers the upsert
 * (POST /bp/batch/<bn>/order-info/) instead of leaving blank space.
 */
export default function BatchOrderHeader({ batchNumber }) {
  const { isDark } = useTheme();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ priority: 'NORMAL' });
  const [saving, setSaving] = useState(false);

  const textColor = isDark ? '#e5e7eb' : '#1f2937';
  const label = isDark ? '#94a3b8' : '#64748b';
  const border = isDark ? '#334155' : '#e5e7eb';
  const bg = isDark ? '#0f172a' : '#f8fafc';

  const load = useCallback(async () => {
    if (!batchNumber) return;
    setLoading(true);
    try {
      const r = await Axios.get(`${bp}/batch/${encodeURIComponent(batchNumber)}/order-info/`, auth());
      setPayload(r?.data || null); setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Order metadata not available');
      setPayload(null);
    }
    setLoading(false);
  }, [batchNumber]);
  useEffect(() => { load(); }, [load]);

  const info = payload?.data || null;
  const batch = payload?.batch || null;
  const expiry = payload?.expiry || null;

  const identity = useMemo(() => {
    if (!batch) return [];
    const product = [batch.product_name, tidy(batch.strength)].filter(Boolean).join(' · ');
    return [
      { label: 'Product', value: product || '—', hint: [batch.product_code && `Code ${batch.product_code}`, batch.dosage_form].filter(Boolean).join(' · '), wide: true },
      { label: batch.is_packaging ? 'MBPR' : 'MBMR', value: batch.mbmr_code || '—', hint: batch.mbmr_version ? `v${batch.mbmr_version}` : '' },
      // No status hint here: the batch page states the status once, in its identity line.
      { label: 'Batch size', value: [num(batch.batch_size) ?? '—', batch.batch_uom].filter(Boolean).join(' ') },
    ];
  }, [batch]);

  const orderFields = useMemo(() => {
    if (!info) return [];
    return [
      { label: 'PO', value: dash(info.po_number) },
      { label: 'SO', value: dash(info.so_number) },
      { label: 'Customer', value: dash(info.customer), wide: true },
      { label: 'Market', value: dash(info.market) },
      { label: 'Campaign', value: dash(info.campaign_no) },
      { label: 'Order qty', value: info.order_quantity ? `${num(info.order_quantity)} ${info.order_uom || ''}`.trim() : '—' },
      { label: 'Planned start', value: fmtDateTime(info.planned_start) },
      { label: 'Planned end', value: fmtDateTime(info.planned_end) },
      { label: 'Target release', value: fmtDate(info.target_release_date) },
    ];
  }, [info]);

  const cell = (f) => (
    <div key={f.label} style={{ padding: '6px 10px', borderRight: `1px solid ${border}`, borderBottom: `1px solid ${border}`, gridColumn: f.wide ? 'span 2' : undefined, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: label }}>{f.label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: textColor, wordBreak: 'break-word' }}>{f.value}</div>
      {f.hint ? <div style={{ fontSize: 10.5, color: label }}>{f.hint}</div> : null}
    </div>
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        planned_start: form.planned_start ? form.planned_start.toISOString() : null,
        planned_end: form.planned_end ? form.planned_end.toISOString() : null,
        target_release_date: form.target_release_date ? form.target_release_date.format('YYYY-MM-DD') : null,
      };
      await Axios.post(`${bp}/batch/${encodeURIComponent(batchNumber)}/order-info/`, body, auth());
      toast.success('Order metadata saved');
      setAddOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save order metadata'); }
    setSaving(false);
  };

  if (!batchNumber) return null;
  if (loading && !payload) return <Skeleton active paragraph={{ rows: 2 }} style={{ padding: 8 }} />;
  if (error && !payload) return <Alert type="warning" showIcon message={error} style={{ marginBottom: 8 }} />;
  if (!payload) return null;

  const provisional = expiry?.is_provisional !== false;
  const expiryValue = expiry?.value || expiry?.stored || expiry?.provisional;
  const pkgRefs = packagingRefs(info?.packaging_order_ref);

  return (
    <div className="bmr-order-header" style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden', background: bg, marginBottom: 12 }}>
      <style>{PRINT_CSS}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 10px', borderBottom: `1px solid ${border}` }}>
        <ClipboardList size={14} style={{ color: '#2563eb' }} />
        <Text strong style={{ color: textColor, fontSize: 13 }}>Order & release header</Text>
        {info?.frozen ? <Tooltip title="The batch has started — edits are deviations"><Tag color="purple" style={{ margin: 0 }}>FROZEN</Tag></Tooltip> : null}
        {batch?.header_frozen ? <Tooltip title="Batch header snapshot taken at issuance (XD1)"><Tag color="geekblue" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}><ShieldCheck size={11} /> header frozen</Tag></Tooltip> : null}
        <span style={{ flex: 1 }} />
        {info?.priority ? <Tag color={(PRIORITY[info.priority] || PRIORITY.NORMAL)[0]} style={{ margin: 0, fontWeight: 600 }}>{(PRIORITY[info.priority] || PRIORITY.NORMAL)[1]} priority</Tag> : null}
        <Tooltip title={provisional
          ? `Provisional — the mfg-date rule ${expiry?.rule || ''} has not fired yet; computed from the planned start + ${expiry?.shelf_life_months ?? '?'} months shelf life`
          : `Mfg ${fmtDate(expiry?.mfg_date)} + ${expiry?.shelf_life_months ?? '?'} months (${expiry?.mfg_date_event || expiry?.rule || ''})`}>
          <Tag color={provisional ? 'warning' : 'success'} style={{ margin: 0, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CalendarClock size={11} /> Exp {fmtDate(expiryValue)}{provisional ? ' · provisional' : ''}
          </Tag>
        </Tooltip>
      </div>

      {identity.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', borderBottom: `1px solid ${border}` }}>
          {identity.map(cell)}
        </div>
      )}

      {!info ? (
        <div className="bmr-order-noprint" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px' }}>
          <Inbox size={15} style={{ color: label }} />
          <Text type="secondary" style={{ fontSize: 12 }}>No order metadata — PO / SO / customer / market / planned window were not captured at issue.</Text>
          <Button size="small" type="link" style={{ padding: 0, height: 'auto' }} onClick={() => { setForm({ priority: 'NORMAL' }); setAddOpen(true); }}>Add order metadata</Button>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            {orderFields.map(cell)}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '6px 10px' }}>
            <span>
              <Text style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: label }}>Change control</Text>{' '}
              <Text style={{ fontSize: 12.5, color: textColor }}>{dash(info.change_control_ref || batch?.recipe_change_control_id)}</Text>
            </span>
            {pkgRefs.length > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <PackageCheck size={12} style={{ color: '#16a34a' }} />
                <Text style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: label }}>Packaging</Text>
                {pkgRefs.map((ref) => (
                  <Link key={ref} to={`/batch-process-details/${encodeURIComponent(ref)}`}>
                    <Tag color="green" style={{ margin: 0, cursor: 'pointer' }}>{ref}</Tag>
                  </Link>
                ))}
              </span>
            )}
            {info.requested_by ? <Text type="secondary" style={{ fontSize: 11.5 }}>Requested by {info.requested_by}</Text> : null}
          </div>
          {info.notes ? (
            <div style={{ padding: '0 10px 8px' }}>
              <Text type="secondary" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap' }}>{info.notes}</Text>
            </div>
          ) : null}
        </>
      )}

      <Modal
        open={addOpen} title={`Order metadata — ${batchNumber}`} onCancel={() => setAddOpen(false)}
        okText="Save" confirmLoading={saving} onOk={save} destroyOnHidden width={640}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[['po_number', 'PO number'], ['so_number', 'SO number'], ['customer', 'Customer'], ['market', 'Market'], ['campaign_no', 'Campaign'], ['change_control_ref', 'Change control ref'], ['packaging_order_ref', 'Packaging batch ref']].map(([k, l]) => (
            <div key={k}>
              <Text style={{ fontSize: 12 }}>{l}</Text>
              <Input size="small" value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <Text style={{ fontSize: 12 }}>Priority</Text>
            <Select size="small" style={{ width: '100%' }} value={form.priority} onChange={(v) => setForm({ ...form, priority: v })}
              options={Object.entries(PRIORITY).map(([k, v]) => ({ value: k, label: v[1] }))} />
          </div>
          <div>
            <Text style={{ fontSize: 12 }}>Planned start</Text>
            <DatePicker size="small" showTime style={{ width: '100%' }} value={form.planned_start || null} onChange={(v) => setForm({ ...form, planned_start: v })} />
          </div>
          <div>
            <Text style={{ fontSize: 12 }}>Planned end</Text>
            <DatePicker size="small" showTime style={{ width: '100%' }} value={form.planned_end || null} onChange={(v) => setForm({ ...form, planned_end: v })} />
          </div>
          <div>
            <Text style={{ fontSize: 12 }}>Target release</Text>
            <DatePicker size="small" style={{ width: '100%' }} value={form.target_release_date || null} onChange={(v) => setForm({ ...form, target_release_date: v })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
